import {
  getLogsToSync,
  getPhotosToSync,
  updateDailyLogSyncStatus,
  updatePhotoSyncStatus,
  removeSyncedDailyLog,
  removeSyncedPhoto,
  getPendingCount,
  type OfflineDailyLog,
  type OfflinePhoto,
} from "./offline-storage";
import { fetchApi } from "./api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "./auth";
import type { QueryClient } from "@tanstack/react-query";

// Sync manager state
let isSyncing = false;
let syncTimeout: ReturnType<typeof setTimeout> | null = null;
let getTokenFn: (() => Promise<string | null>) | null = null;
let queryClient: QueryClient | null = null;
let isInitialized = false;

export type SyncStatus = {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime?: string;
  lastError?: string;
};

// Event listeners for sync status
const listeners: Set<(status: SyncStatus) => void> = new Set();

/**
 * Subscribe to sync status changes
 */
export function subscribeSyncStatus(callback: (status: SyncStatus) => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * Notify all listeners of status change
 */
async function notifyListeners(): Promise<void> {
  const pendingCount = await getPendingCount();
  const status: SyncStatus = {
    isSyncing,
    pendingCount,
    lastSyncTime: new Date().toISOString(),
  };
  listeners.forEach((listener) => listener(status));
}

/**
 * Initialize sync manager with auth token function and query client
 */
export function initSyncManager(
  getToken: () => Promise<string | null>,
  qc?: QueryClient
): void {
  // Always update these (they may change)
  getTokenFn = getToken;
  if (qc) {
    queryClient = qc;
  }

  // Only add event listeners once
  if (isInitialized) {
    return;
  }
  isInitialized = true;

  // Listen for online/offline events
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  // Initial sync if online
  if (navigator.onLine) {
    scheduleSyncWithDelay(2000);
  }
}

/**
 * Cleanup sync manager (call on unmount)
 */
export function cleanupSyncManager(): void {
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }
  isInitialized = false;
}

/**
 * Handle coming back online
 */
function handleOnline(): void {
  console.log("[SyncManager] Back online, scheduling sync...");
  scheduleSyncWithDelay(3000); // Wait 3 seconds for connection to stabilize
}

/**
 * Handle going offline
 */
function handleOffline(): void {
  console.log("[SyncManager] Went offline, cancelling pending sync...");
  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }
}

/**
 * Schedule a sync after a delay (debounced)
 */
function scheduleSyncWithDelay(delayMs: number): void {
  if (syncTimeout) {
    clearTimeout(syncTimeout);
  }
  syncTimeout = setTimeout(() => {
    syncAll();
  }, delayMs);
}

/**
 * Trigger immediate sync
 */
export async function triggerSync(): Promise<void> {
  if (syncTimeout) {
    clearTimeout(syncTimeout);
    syncTimeout = null;
  }
  await syncAll();
}

/**
 * Main sync function - syncs all pending items
 */
async function syncAll(): Promise<void> {
  if (isSyncing) {
    console.log("[SyncManager] Sync already in progress, skipping...");
    return;
  }

  if (!navigator.onLine) {
    console.log("[SyncManager] Offline, skipping sync");
    return;
  }

  isSyncing = true;
  await notifyListeners();

  try {
    // Get auth headers
    const headers = CLERK_AVAILABLE && getTokenFn
      ? await getClerkAuthHeaders(getTokenFn)
      : getDemoAuthHeaders();

    // Sync daily logs first (photos depend on them)
    const logsToSync = await getLogsToSync();
    console.log(`[SyncManager] Syncing ${logsToSync.length} daily logs...`);

    for (const log of logsToSync) {
      await syncDailyLog(log, headers);
    }

    // Then sync photos
    const photosToSync = await getPhotosToSync();
    console.log(`[SyncManager] Syncing ${photosToSync.length} photos...`);

    for (const photo of photosToSync) {
      await syncPhoto(photo, headers);
    }

    console.log("[SyncManager] Sync complete!");
  } catch (error) {
    console.error("[SyncManager] Sync failed:", error);
  } finally {
    isSyncing = false;
    await notifyListeners();
  }
}

/**
 * Sync a single daily log to the server
 */
async function syncDailyLog(log: OfflineDailyLog, headers: HeadersInit): Promise<void> {
  try {
    await updateDailyLogSyncStatus(log.id, "syncing");

    // Prepare the payload - use 'temperature' to match API schema
    // Only include weather if we have values (Zod expects undefined, not null)
    const payload: Record<string, unknown> = {
      date: log.date,
      workPerformed: log.workPerformed,
      offlineId: log.id, // Send offline ID so server can dedupe
    };

    // Only add weather if we have data
    if (log.weatherTemp !== null || log.weatherConditions !== null) {
      payload["weather"] = {
        ...(log.weatherTemp !== null && { temperature: log.weatherTemp }),
        ...(log.weatherConditions !== null && { conditions: log.weatherConditions }),
      };
    }

    // Only add optional fields if they have values
    if (log.crewCount !== null) {
      payload["crewCount"] = log.crewCount;
    }
    if (log.issues) {
      payload["issues"] = log.issues;
    }

    const response = await fetchApi<{ id: string }>(
      `/api/projects/${log.projectId}/daily-logs`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }
    );

    // Success - mark as synced and remove from offline storage
    console.log(`[SyncManager] Daily log ${log.id} synced successfully as ${response.id}`);
    await updateDailyLogSyncStatus(log.id, "synced");
    await removeSyncedDailyLog(log.id);

    // Invalidate queries to update UI
    if (queryClient) {
      await queryClient.invalidateQueries({
        queryKey: ["projects", log.projectId, "daily-logs"],
      });
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";

    // Check if this is a 409 conflict - means log already exists on server
    // This can happen if:
    // 1. This is a retry of a previously synced log (idempotent case - should now return 200)
    // 2. Another user created a log for the same date (real conflict)
    // Either way, we should mark as synced and remove from offline storage
    // The server's idempotent handling ensures we get the existing log back
    if (errorMessage.includes("409") || errorMessage.includes("already exists")) {
      console.log(`[SyncManager] Daily log ${log.id} already exists on server (409 conflict) - marking as synced`);
      await updateDailyLogSyncStatus(log.id, "synced");
      await removeSyncedDailyLog(log.id);

      // Invalidate queries to update UI with server version
      if (queryClient) {
        await queryClient.invalidateQueries({
          queryKey: ["projects", log.projectId, "daily-logs"],
        });
      }
    } else {
      // Other errors (network, auth, etc.) should be retried
      console.error(`[SyncManager] Failed to sync daily log ${log.id}:`, errorMessage);
      await updateDailyLogSyncStatus(log.id, "error", errorMessage);
    }
  }
}

/**
 * Sync a single photo to the server
 */
async function syncPhoto(photo: OfflinePhoto, headers: HeadersInit): Promise<void> {
  try {
    await updatePhotoSyncStatus(photo.id, "syncing");

    // Create FormData for photo upload
    const formData = new FormData();
    formData.append("file", photo.blob, photo.filename);
    if (photo.caption) {
      formData.append("caption", photo.caption);
    }
    formData.append("offlineId", photo.id);

    // Note: Don't set Content-Type header for FormData - browser will set it with boundary
    const uploadHeaders: HeadersInit = {};
    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        if (key.toLowerCase() !== "content-type") {
          (uploadHeaders as Record<string, string>)[key] = value;
        }
      });
    } else if (typeof headers === "object") {
      Object.entries(headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== "content-type" && typeof value === "string") {
          (uploadHeaders as Record<string, string>)[key] = value;
        }
      });
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL || "http://localhost:8787"}/api/projects/${photo.projectId}/daily-logs/${photo.dailyLogId}/photos`,
      {
        method: "POST",
        headers: uploadHeaders,
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    // Success - mark as synced and remove from offline storage
    console.log(`[SyncManager] Photo ${photo.id} synced successfully`);
    await updatePhotoSyncStatus(photo.id, "synced");
    await removeSyncedPhoto(photo.id);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    console.error(`[SyncManager] Failed to sync photo ${photo.id}:`, errorMessage);
    await updatePhotoSyncStatus(photo.id, "error", errorMessage);
  }
}

/**
 * Get current sync status
 */
export async function getSyncStatus(): Promise<SyncStatus> {
  const pendingCount = await getPendingCount();
  return {
    isSyncing,
    pendingCount,
    lastSyncTime: new Date().toISOString(),
  };
}

/**
 * Check if there are pending items to sync
 */
export async function hasPendingItems(): Promise<boolean> {
  const count = await getPendingCount();
  return count > 0;
}
